import { cn } from "@/lib/utils";

type LogoVariant = "color" | "mono" | "dark";

export type LogoProps = {
  variant?: LogoVariant;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
};

/**
 * The rmnlng logo: lowercase Latin `r` next to Cyrillic `я` on an accent-blue
 * rounded square. Extracted from the design handoff (icon.svg).
 *
 * Sizing: the SVG uses a 200×200 viewBox; size it via `className`
 * (e.g. `className="size-12"`). The brand square corners scale with the box.
 */
export function Logo({ variant = "color", className, title = "rmnlng", style }: LogoProps) {
  const palette = paletteFor(variant);
  return (
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={cn("shrink-0", className)}
      style={style}
    >
      <title>{title}</title>
      <rect width="200" height="200" rx="44" fill={palette.bg} />
      <g fill="none" stroke={palette.fg} strokeWidth={14} strokeLinecap="round" strokeLinejoin="round">
        <path d="M62 148 V72" />
        <path d="M62 92 Q76 68 98 72" />
      </g>
      <g fill={palette.fg}>
        <path d="M158 72 H120 Q100 72 100 92 Q100 110 118 112 L100 148 H118 L134 116 H144 V148 H158 Z M144 86 V102 H122 Q114 102 114 94 Q114 86 122 86 Z" />
      </g>
    </svg>
  );
}

function paletteFor(variant: LogoVariant): { bg: string; fg: string } {
  switch (variant) {
    case "mono":
      return { bg: "#ffffff", fg: "oklch(55% 0.15 245)" };
    case "dark":
      return { bg: "oklch(72% 0.14 245)", fg: "oklch(15% 0.02 245)" };
    case "color":
    default:
      return { bg: "oklch(55% 0.15 245)", fg: "#ffffff" };
  }
}
